import CustomDatePicker from "@/components/CustomDatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Search } from "lucide-react";
import PropertiesMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect";
import OrganizationsMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect";
import { useMemo } from "react";

interface ExpenseFiltersProps {
  categories: { id: string; name: string }[];
  tags: { id: string; name: string; color: string }[];
  searchTerm: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  hasActiveFilters: boolean;
  properties: { id: string; shortName?: string; title?: string }[];
  organizations: any[];
  contracts: any[];
  selectedCategory: string;
  selectedTag: string;
  selectedOrganizations: string[];
  selectedProperties: string[];
  clearFilters: () => void;
  setSearchTerm: (value: string) => void;
  setDateFrom: (value: Date | undefined) => void;
  setDateTo: (value: Date | undefined) => void;
  setSelectedCategory: (value: string) => void;
  setSelectedTag: (value: string) => void;
  setSelectedOrganizations: (value: string[]) => void;
  setSelectedProperties: (value: string[]) => void;
}

const ExpenseFilters = ({
  categories,
  tags,
  searchTerm,
  dateFrom,
  dateTo,
  properties,
  organizations,
  contracts,
  selectedCategory,
  selectedTag,
  selectedOrganizations,
  selectedProperties,
  hasActiveFilters,
  clearFilters,
  setSearchTerm,
  setDateFrom,
  setDateTo,
  setSelectedCategory,
  setSelectedTag,
  setSelectedOrganizations,
  setSelectedProperties,
}: ExpenseFiltersProps) => {
  // Filter properties based on selected organizations
  const filteredProperties = useMemo(() => {
    if (selectedOrganizations.length === 0) {
      return properties;
    }
    
    const propertyIdsInOrganizations = contracts
      .filter((contract: any) => 
        contract.status === 'active' && 
        selectedOrganizations.includes(contract.organization_id)
      )
      .map((contract: any) => contract.property_id);
    
    return properties.filter((property: any) => 
      propertyIdsInOrganizations.includes(property.id)
    );
  }, [properties, selectedOrganizations, contracts]);

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-[240px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0"
          />
        </div>

        <OrganizationsMultiSelect
          organizations={organizations}
          selectedOrganizations={selectedOrganizations}
          onSelectionChange={setSelectedOrganizations}
        />

        <PropertiesMultiSelect
          properties={filteredProperties as any}
          selectedProperties={selectedProperties}
          onSelectionChange={setSelectedProperties}
        />

        <Select
          value={selectedCategory || "all"}
          onValueChange={(value) =>
            setSelectedCategory(value === "all" ? "" : value)
          }
        >
          <SelectTrigger className="w-[180px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0">
            <SelectValue placeholder="Todas las categorías" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>

            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedTag || "all"}
          onValueChange={(value) =>
            setSelectedTag(value === "all" ? "" : value)
          }
        >
          <SelectTrigger className="w-[180px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0">
            <SelectValue placeholder="Todas las etiquetas" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="all">Todas las etiquetas</SelectItem>

            {tags.map((tag) => (
              <SelectItem key={tag.id} value={tag.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <CustomDatePicker
          selected={dateFrom}
          onSelect={setDateFrom}
          placeholder="Fecha desde"
          isAdminContext={true}
          variant="pill"
          showIcon
          className="w-[160px]"
        />

        <CustomDatePicker
          selected={dateTo}
          onSelect={setDateTo}
          placeholder="Fecha hasta"
          isAdminContext={true}
          variant="pill"
          showIcon
          className="w-[160px]"
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={clearFilters}
            size="icon"
            className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ExpenseFilters;
